import React, { useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import PropTypes from 'prop-types'

import {
  Box,
  Button,
  DashboardModule,
  FormField,
  ListItem,
  LoadingModule,
  StatusBadge,
} from '@campaignhub/suit-theme'

import { useSetState } from '@campaignhub/react-hooks'
import PageContext from '@contexts/pageContext'

import useServiceJobDetail from '@hooks/useServiceJobDetail'

const upsertServiceJobDetail = (serviceJobDetail, createFn, setState) => {
  createFn(serviceJobDetail).then(({ data, success, errors }) => {
    if (!success) return toast.warning(errors[0])
    return setState({ stripeRefundDetailsId: data.id, refundComment: '' })
  })
}

const undoRefundServiceJobDetail = (serviceJobDetail, undoRefundFn, setState) => {
  undoRefundFn(serviceJobDetail).then(({ success, errors }) => {
    if (!success) return toast.warning(errors[0])
    return setState({ stripeRefundDetailsId: null, refundComment: '' })
  })
}

const defaultState = {
  isRefundCommentValid: true,
  refundComment: '',
  refundTransactionId: '',
  stripeRefundDetailsId: null,
}

const StripeRefund = (props) => {
  const { canRefund = false, hidden } = props

  const { serviceJob } = useContext(PageContext)

  const entities = useSelector(reduxState => reduxState.entities)
  const { serviceJobDetails } = entities

  const { loading: serviceJobLoading } = useSelector(reduxState => reduxState.serviceJobs)

  const [state, setState] = useSetState(defaultState)
  const { isRefundCommentValid, refundComment, refundTransactionId, stripeRefundDetailsId } = state

  const {
    callbacks: {
      upsertServiceJobDetail: createServiceJobDetailFn,
      undoRefundServiceJobDetail: undoRefundServiceJobDetailFn,
    },
    creating,
    deleting,
  } = useServiceJobDetail()

  const refund = () => {
    setState({ isRefundCommentValid: refundComment.length > 0 })

    if (refundComment.length > 0) {
      if (stripeRefundDetailsId) {
        const payload = {
          id: stripeRefundDetailsId,
          serviceJobId: serviceJob.id,
          reason: refundComment.trim(),
        }

        undoRefundServiceJobDetail(payload, undoRefundServiceJobDetailFn, setState)
      }
      else {
        const value = { refundReason: refundComment.trim(), stripeRefundId: (refundTransactionId || '').trim() }
        const payload = {
          serviceJobId: serviceJob.id,
          fieldName: 'StripeRefundDetails',
          value: `${JSON.stringify(value)}`,
        }

        upsertServiceJobDetail(payload, createServiceJobDetailFn, setState)
      }

    }
  }

  useEffect(() => {
    const refundID = serviceJob.details?.find(x => Object.values(serviceJobDetails)?.find(y => y.id === x && y.entityFieldType.name === 'StripeRefundDetails'))
    if (refundID) {
      setState({ stripeRefundDetailsId: refundID })
    }
  }, [serviceJobDetails, serviceJob?.details])

  if (hidden) return null

  return (
    <>
      <LoadingModule loading={serviceJobLoading} />
      {!serviceJobLoading && (
        <DashboardModule
          title="Stripe Payment"
          headerRight={(

            <Box display="block" width="auto" textAlign="end">
              {stripeRefundDetailsId ? (
                <StatusBadge color="#f32929ff" ghost text="Refunded" />
              ) : <StatusBadge color="#06a347ff" ghost text="Paid" />}
            </Box>
          )}
        >
          {canRefund ? (
            <Box flexDirection="column">
              <ListItem
                boxProps={{
                  borderBottom: '1px solid',
                  padding: 'large',
                }}
                flexDirection="column"
                style={{ cursor: 'pointer' }}
                disableHover
              >
                <Box flexDirection="column">
                  {stripeRefundDetailsId ? null : (
                    <Box flexDirection="column">
                      <FormField label="Transaction ID">
                        <input
                          type="text"
                          value={refundTransactionId}
                          onChange={e => (setState({ refundTransactionId: e.target.value }))}
                          placeholder="Enter Transaction ID (Optional)"
                          disabled={stripeRefundDetailsId}
                        />
                      </FormField>
                    </Box>
                  )}
                  <Box flexDirection="column" paddingTop="small" paddingBottom="small">
                    <FormField label={`${stripeRefundDetailsId ? 'Undo Reason' : ''}`} errorMessage={!isRefundCommentValid ? 'Required' : ''}>
                      <textarea
                        type="text"
                        value={refundComment}
                        onChange={e => (setState({ refundComment: e.target.value }))}
                        placeholder={`Reason required for ${stripeRefundDetailsId ? 'undoing' : 'processing'} refund`}
                        style={{ height: 80, resize: 'vertical' }}
                      />
                    </FormField>
                  </Box>
                  <Button buttonStyle="secondaryUtility" size="medium" onClick={refund} loading={creating || deleting}>
                    {stripeRefundDetailsId ? 'Undo Refund' : 'Refund'}
                  </Button>
                </Box>
              </ListItem>
            </Box>
          ) : null}
        </DashboardModule>
      )}
    </>
  )
}

StripeRefund.propTypes = {
  canRefund: PropTypes.bool,
  hidden: PropTypes.bool,
}

export default StripeRefund
